import {
  Badge,
  Button,
  Card,
  CardContent,
  Input,
  Label,
} from "@exhousing/design-system";
import { Plus } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import { useMe } from "@/hooks/api/useAuth";
import {
  useAssignRole,
  useCreateUser,
  useRoles,
  useSetUserActive,
  useUsers,
} from "@/hooks/api/useUsers";

// Roles an org admin may assign (super_admin / corporate_client are not self-assignable).
const ASSIGNABLE = new Set([
  "org_admin",
  "manager",
  "sales_manager",
  "account_manager",
  "inspector",
  "viewer",
]);

export function TeamPage() {
  const { data: me } = useMe();
  const { data: users } = useUsers();
  const { data: roles } = useRoles();
  const create = useCreateUser();
  const setActive = useSetUserActive();
  const assignRole = useAssignRole();

  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    email: "",
    password: "",
    role_code: "account_manager",
  });

  const assignable = (roles ?? []).filter((r) => ASSIGNABLE.has(r.code));

  function submit(e: React.FormEvent) {
    e.preventDefault();
    create.mutate(
      {
        email: form.email,
        password: form.password,
        first_name: form.first_name,
        last_name: form.last_name,
        role_codes: [form.role_code],
      },
      {
        onSuccess: () => {
          toast.success("Team member added");
          setShowForm(false);
          setForm({
            first_name: "",
            last_name: "",
            email: "",
            password: "",
            role_code: "account_manager",
          });
        },
        onError: (err) => toast.error(err.message),
      },
    );
  }

  return (
    <div className="space-y-6">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl font-medium tracking-tight">Team</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {users ? `${users.length} members` : "Loading…"}
          </p>
        </div>
        <Button onClick={() => setShowForm((s) => !s)}>
          <Plus className="size-4" />
          Add member
        </Button>
      </header>

      {showForm && (
        <Card className="animate-fade-in-up">
          <CardContent className="pt-6">
            <form onSubmit={submit} className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="fn">First name</Label>
                <Input
                  id="fn"
                  value={form.first_name}
                  onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="ln">Last name</Label>
                <Input
                  id="ln"
                  value={form.last_name}
                  onChange={(e) => setForm({ ...form, last_name: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="em">Email</Label>
                <Input
                  id="em"
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="pw">Temporary password</Label>
                <Input
                  id="pw"
                  type="text"
                  minLength={8}
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="role">Role</Label>
                <select
                  id="role"
                  className="h-10 w-full rounded-md border border-input bg-background px-2.5 text-sm outline-none focus:border-primary"
                  value={form.role_code}
                  onChange={(e) => setForm({ ...form, role_code: e.target.value })}
                >
                  {assignable.map((r) => (
                    <option key={r.code} value={r.code}>
                      {r.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex items-end">
                <Button type="submit" disabled={create.isPending}>
                  {create.isPending ? "Adding…" : "Add member"}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent className="overflow-x-auto p-0">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left text-xs uppercase tracking-wide text-muted-foreground">
                <th className="px-6 py-3">Member</th>
                <th className="px-6 py-3">Role</th>
                <th className="px-6 py-3">Status</th>
                <th className="px-6 py-3" />
              </tr>
            </thead>
            <tbody>
              {users?.map((u) => {
                const isSelf = u.id === me?.id;
                return (
                  <tr key={u.id} className="border-b border-border/60 last:border-0">
                    <td className="px-6 py-3">
                      <p className="font-medium">
                        {u.first_name} {u.last_name}
                      </p>
                      <p className="text-xs text-muted-foreground">{u.email}</p>
                    </td>
                    <td className="px-6 py-3">
                      <select
                        className="h-8 rounded-md border border-input bg-background px-2 text-sm outline-none focus:border-primary disabled:opacity-50"
                        value={u.roles[0] ?? ""}
                        disabled={isSelf || assignRole.isPending}
                        onChange={(e) =>
                          assignRole.mutate(
                            { id: u.id, role_code: e.target.value },
                            {
                              onSuccess: () => toast.success("Role updated"),
                              onError: (err) => toast.error(err.message),
                            },
                          )
                        }
                      >
                        {!u.roles.length && <option value="">— none —</option>}
                        {assignable.map((r) => (
                          <option key={r.code} value={r.code}>
                            {r.name}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="px-6 py-3">
                      <Badge tone={u.is_active ? "success" : "neutral"} dot>
                        {u.is_active ? "Active" : "Inactive"}
                      </Badge>
                    </td>
                    <td className="px-6 py-3 text-right">
                      {!isSelf && (
                        <Button
                          variant="ghost"
                          size="sm"
                          disabled={setActive.isPending}
                          onClick={() =>
                            setActive.mutate(
                              { id: u.id, is_active: !u.is_active },
                              {
                                onError: (err) => toast.error(err.message),
                              },
                            )
                          }
                        >
                          {u.is_active ? "Deactivate" : "Activate"}
                        </Button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}
